import Box from '@components/base/Box'
import { Tab, Tabs } from '@components/base/Tabs'
import Text from '@components/base/Text'
import { CheckboxInput } from '@components/inputs/CheckboxInput'
import { DropdownInput } from '@components/inputs/DropdownInput'
import { DateTimeInput } from '@components/inputs/DateTimeInput'
import RichTextEditor from '@components/inputs/RichTextEditor/RichTextEditor'
import { TextInput } from '@components/inputs/TextInput'
import { ToggleInput } from '@components/inputs/ToggleInput'
import {
  EventEmailTrigger,
  NotificationStatus,
} from '@graphql/__generated/graphql'
import { CreateEventContext } from '@libs/CreateEventContext'
import { find } from 'lodash'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import {
  EmailTemplateListItem,
  IEmailBuilder,
  SCHEDULED_EMAIL_TRIGGER,
} from './emailbuilder.type'
import { EmailPreview } from './emailPreview'
import useLocalStorage from '@libs/useLocalStorage'
import { v4 } from 'uuid'

type EmailContentCacheParams = {
  type?: IEmailBuilder['type']
  scopeId?: string | null
  template?: Partial<EmailTemplateListItem>
}

export const getEmailContentCacheKey = ({
  type = 'event',
  scopeId,
  template,
}: EmailContentCacheParams) => {
  const trigger = template?.trigger ?? EventEmailTrigger.Confirmation
  const templateId =
    template?.templateId ??
    (template?.editIndex === 0 || template?.editIndex
      ? `index-${template.editIndex}`
      : `new-${trigger}`)

  return ['email-content-cache', type, scopeId ?? 'draft', templateId].join('-')
}

export const clearEmailContentCache = (params: EmailContentCacheParams) => {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(getEmailContentCacheKey(params))
}

const EmailBuilderUI = ({
  enablePreview,
  onSubmit,
  setError,
  template,
  decorators,
  data,
  type = 'event',
  cacheScopeId,
}: IEmailBuilder) => {
  const { formData, setFormData, validationRules, errors, setErrors } =
    useContext(CreateEventContext)

  const [emailTrigger, setEmailTrigger] = useState<EventEmailTrigger>(
    template?.trigger ?? EventEmailTrigger.Manual
  )
  const [scheduledAt, setScheduledAt] = useState(
    template?.schedule?.runAt ?? ''
  )
  const [scheduleId] = useState(
    template?.schedule?.status === NotificationStatus.Sent
      ? v4()
      : template?.schedule?.scheduleId || v4()
  )

  const initialContent = template?.content ?? '<p>Write your email here.</p>'

  const scopeId =
    cacheScopeId ??
    (type === 'membership'
      ? (data as any)?.membershipId
      : ((data as any)?.eventId ?? formData?.eventId))

  const emailContentCacheKey = getEmailContentCacheKey({
    type,
    scopeId,
    template,
  })

  const [emailContentCache, setEmailContentCache] = useLocalStorage<{
    data: string
    markup: string
  } | null>(emailContentCacheKey, null)

  const [emailContent, setEmailContent] = useState({
    data: emailContentCache?.data ?? template?.data ?? '',
    markup: emailContentCache?.markup ?? initialContent,
  })

  const [emailTitle, setEmailTitle] = useState(template?.title ?? 'Reminder')

  const [emailStatus, setEmailStatus] = useState<boolean>(
    template?.enabled || true
  )
  const [showPreview, setShowPreview] = useState<boolean>(!!onSubmit)
  const isScheduled = emailTrigger === SCHEDULED_EMAIL_TRIGGER
  const scheduleError = isScheduled
    ? !scheduledAt
      ? 'Choose when this email should be sent.'
      : !moment(scheduledAt).isAfter(moment())
        ? 'Scheduled send time must be in the future.'
        : undefined
    : undefined

  useEffect(() => {
    if (!!onSubmit) {
      return onSubmit({
        trigger: emailTrigger,
        schedule:
          isScheduled && scheduledAt
            ? {
                scheduleId,
                runAt: scheduledAt,
                status:
                  template?.schedule?.runAt === scheduledAt &&
                  template.schedule.scheduleId === scheduleId &&
                  template.schedule.status !== NotificationStatus.Failed
                    ? (template.schedule.status ??
                      NotificationStatus.Processing)
                    : NotificationStatus.Processing,
              }
            : undefined,
        content: emailContent.markup,
        data: emailContent.data,
        enabled: emailStatus,
        title: emailTitle,
        editIndex: template?.editIndex,
        originalTitle: template?.originalTitle,
        legacyScheduleTimestamp: template?.legacyScheduleTimestamp,
      })
    }

    // return console.log(
    //   `[EmailBuilder]: Custom submit handler detected bypassing direct update.`
    // )

    const payload =
      !!formData?.emailTemplates && formData?.emailTemplates?.length > 0
        ? formData?.emailTemplates?.map((m) => {
            if (m.trigger !== emailTrigger) return m
            return {
              ...m,
              content: emailContent.markup,
              data: emailContent.data,
              enabled: emailStatus,
              title: emailTitle,
            }
          })
        : [
            {
              trigger: emailTrigger,
              content: emailContent.markup,
              data: emailContent.data,
              enabled: emailStatus,
              title: emailTitle,
            },
          ]
    setFormData({ ...formData, emailTemplates: payload })
  }, [emailContent, emailStatus, emailTrigger, emailTitle, scheduledAt])
  // console.log({ decorators })

  return (
    <Box className="size-full">
      <Box show={!onSubmit} className="flex items-center justify-between">
        <DropdownInput
          options={[
            {
              label: 'Confirmation Email',
              value: EventEmailTrigger.Confirmation,
            },
          ]}
          onChange={(op) => setEmailTrigger(op)}
          className="mt-6 mb-4"
          maxWidth="300px"
          label="Select Email Template"
        />
        <CheckboxInput
          show={!onSubmit}
          value={showPreview}
          onChange={(data) => {
            setShowPreview(data)
          }}
          className="bg-transparent rounded text-sm border-box"
          border=""
          label=""
          required
        >
          <Text>Show Email Preview</Text>
        </CheckboxInput>

        <CheckboxInput
          show={!onSubmit}
          value={emailStatus}
          onChange={(data) => {
            setEmailStatus(data)
          }}
          className="bg-transparent rounded text-sm border-box"
          border=""
          label=""
          required
        >
          <Text>Use this template</Text>
        </CheckboxInput>
      </Box>
      <Box className="flex flex-col md:flex-row h-full">
        <Box className="flex-1 md:flex-2 flex flex-col">
          <Box className="flex flex-col">
            <TextInput
              validations={{
                minLength: {
                  value: 2,
                  message: 'Email template title must be at least 2 characters',
                },
              }}
              label="Subject Line"
              type="text"
              placeholder="Reminder Template"
              onChange={(data) => {
                setEmailTitle(data)
                if (
                  formData?.emailTemplates?.find(
                    (f) =>
                      f.title?.toLowerCase() === (data as string).toLowerCase()
                  )
                )
                  setErrors([
                    ...errors,
                    {
                      step: 'notifications',
                      eleId: 'EmailTemplateTitle',
                      message:
                        'You must use a unique name for each email template',
                    },
                  ])
                else {
                  setErrors([
                    ...errors.filter((f) => f.eleId !== 'EmailTemplateTitle'),
                  ])
                }
              }}
              value={emailTitle}
              error={
                errors.find((e) => e.eleId === 'EmailTemplateTitle')?.message
              }
            />
            <Box className="flex items-center lg:items-start mb-2 lg:mb-0">
              <Box className="flex flex-col lg:flex-row items-start">
                <DropdownInput
                  options={[
                    {
                      label: 'Registration Confirmation',
                      value: EventEmailTrigger.Confirmation,
                      show: emailTrigger === EventEmailTrigger.Confirmation,
                      disabled: true,
                    },
                    {
                      label: 'Manual Send',
                      value: EventEmailTrigger.Manual,
                      disabled: emailTrigger === EventEmailTrigger.Confirmation,
                      show: emailTrigger !== EventEmailTrigger.Confirmation,
                    },
                    ...(type === 'event' && !!onSubmit
                      ? [
                          {
                            label: 'Scheduled Send',
                            value: SCHEDULED_EMAIL_TRIGGER,
                            disabled:
                              emailTrigger === EventEmailTrigger.Confirmation,
                            show:
                              emailTrigger !== EventEmailTrigger.Confirmation,
                          },
                        ]
                      : []),
                  ]}
                  onChange={(op) => setEmailTrigger(op)}
                  className="mt-6 mb-4 mr-4"
                  value={emailTrigger}
                  autoWidth={true}
                  label="Send Options"
                />
                <Box className="text-sm font-light text-gray-500">
                  <Tabs activeTitle={emailTrigger}>
                    <Tab title={EventEmailTrigger.Manual}>
                      <span className="font-semibold">
                        Select where this email template will be used.
                      </span>
                      <br /> {"'"}Manual Send
                      {"' "}
                      creates an email template that can be manually sent to all
                      attendees.
                    </Tab>
                    <Tab title={EventEmailTrigger.Confirmation}>
                      <span className="font-semibold">
                        Select where this email template will be used.
                      </span>
                      <br /> {"'"} Registration Confirmation
                      {"' "}
                      creates an email template that will be sent to attendee
                      who register for your event.
                    </Tab>
                    <Tab title={SCHEDULED_EMAIL_TRIGGER}>
                      <span className="font-semibold">
                        Send this email automatically.
                      </span>
                      <br /> Choose a future date and time. Current attendees
                      and speakers will receive the latest saved version of this
                      email.
                    </Tab>
                  </Tabs>
                </Box>
              </Box>
            </Box>
            <Box
              show={isScheduled}
              className="mb-4 max-w-md rounded-2xl border border-gray-300 p-4"
            >
              <DateTimeInput
                id="email-schedule-time"
                label="Send date and time"
                required
                value={scheduledAt}
                onChange={(value) => setScheduledAt(value)}
                placeholderText="Choose a future date and time"
                error={scheduleError}
                filterDate={(rawDate) =>
                  !moment(rawDate).isBefore(moment(), 'day')
                }
                filterTime={(rawTime) => moment(rawTime).isAfter(moment())}
              />
              <Text className="text-sm text-gray-600">
                Times use your current timezone and are saved as UTC.
              </Text>
            </Box>
          </Box>
          <Box className="flex-1 flex flex-col ">
            <RichTextEditor
              key={template?.trigger}
              decorators={decorators}
              resizeEnabled={false}
              tooltip="Email Content"
              testid="email-content-input"
              className="relative max-h-[44vh] flex"
              setError={setError}
              label="Email Content"
              placeholder={`Write your email here`}
              value={emailContent.data}
              onChange={(res) => {
                const { data, markup } = res
                if (!!data && !!markup) {
                  setEmailContent({ data, markup })
                  setEmailContentCache({ data, markup })
                }
              }}
              htmlOutput
            />
          </Box>
        </Box>
        <Box className="relative flex-1 lg:flex-3 ml-2 my-4 min-h-[44vh] flex">
          <EmailPreview
            resizeEnabled={false}
            show={showPreview}
            content={emailContent.markup}
            data={data}
            type={type}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default EmailBuilderUI
